import { FC, useState, useEffect } from 'react'

import styled from 'styled-components'
import { useDisclosure } from '@mantine/hooks'
import { useDebounce } from 'use-debounce'
import { useForm } from 'react-hook-form'
import { notifications } from '@mantine/notifications'

import {
  AddClientBody,
  Client,
  useAddClientMutation,
  useArchiveClientMutation,
  useGetAllClientsQuery,
  useUpdateClientMutation,
} from 'src/Redux/api/clientsApi.ts'
import { PageTitle } from 'src/Components/PageTitle/index.tsx'
import { SearchBlock } from 'src/Pages/Clients/SearchBlock.tsx'
import { AddClientModal } from 'src/Pages/Clients/AddClientsModal.tsx'
import { ClientsTable } from 'src/Pages/Clients/ClientsTable.tsx'
import { EditClientsModal } from 'src/Pages/Clients/EditClientsModal.tsx'
import { Role, useGetProfileQuery } from 'src/Redux/api/authApi.ts'
import { device } from 'src/Helpers/constants.ts'
import { TableHeader } from 'src/Pages/Clients/TableHeader.tsx'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 36px;
  position: relative;
  height: 100%;
  overflow: hidden;
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 50px;
  position: static;

  @media ${device.tabletMax} {
    gap: 20px;
    width: 100%;

    div {
      width: 100%;
    }
  }
`

export interface SearchClientFormData {
  searchString: string
}

export const Clients: FC = () => {
  const [isAddModalOpened, { open: openAddModal, close: closeAddModal }] =
    useDisclosure(false)

  const [isEditModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [clientToEdit, setClientToEdit] = useState<Client | null>(null)

  const { control, watch } = useForm<SearchClientFormData>()

  const { searchString } = watch()

  const [debouncedSearchValue] = useDebounce(searchString, 1000)

  // Сбрасываем pageNumber на 1 при изменении поискового запроса
  useEffect(() => {
    setPageNumber(1)
  }, [debouncedSearchValue])

  const { data: clientsResponse, isLoading } = useGetAllClientsQuery({
    searchString: debouncedSearchValue,
    pageNumber: pageNumber,
    limit: 20,
  })

  const [addClient] = useAddClientMutation()
  const [archiveClient] = useArchiveClientMutation()
  const [updateClient] = useUpdateClientMutation()
  const { data: user } = useGetProfileQuery()
  const isArchiveClientAvailable =
    user?.role === Role.ADMIN || user?.role === Role.LEAD_MANAGER

  const onAddClient = (client: AddClientBody): void => {
    addClient(client)
      .unwrap()
      .then(() => {
        closeAddModal()
        notifications.show({
          title: 'Клиент успешно добавлен',
          message: '',
          position: 'top-right',
        })
      })
      .catch((error) => {
        notifications.show({
          title: 'Ошибка при добавлении клиента',
          message:
            error?.data ||
            error?.data?.message ||
            error?.message ||
            'Произошла неизвестная ошибка',
          color: 'red',
          position: 'top-right',
        })
      })
  }

  const onArchiveClient = (id: number): void => {
    archiveClient(id)
      .unwrap()
      .then(() => {
        notifications.show({
          title: 'Клиент успешно удален',
          message: '',
          position: 'top-right',
        })
      })
  }

  const onEditClient = (client: AddClientBody): void => {
    if (clientToEdit) {
      updateClient({ id: clientToEdit.id, data: client })
        .unwrap()
        .then(() => {
          closeEditModal()
          notifications.show({
            title: 'Клиент успешно изменен',
            message: '',
            position: 'top-right',
          })
        })
        .catch((error) => {
          notifications.show({
            title: 'Ошибка при изменении клиента',
            message:
              error?.data ||
              error?.data?.message ||
              error?.message ||
              'Произошла неизвестная ошибка',
            color: 'red',
            position: 'top-right',
          })
        })
    }
  }

  const onLoadMore = (): void => {
    setPageNumber(pageNumber + 1)
  }

  const onClickOpenEditModal = (client: Client): void => {
    setClientToEdit(client)
    openEditModal()
  }

  return (
    <>
      <AddClientModal
        onClose={closeAddModal}
        onAddClient={onAddClient}
        isOpened={isAddModalOpened}
      />

      <EditClientsModal
        onClose={closeEditModal}
        onEditClient={onEditClient}
        isOpened={isEditModalOpened}
        clientToEdit={clientToEdit}
      />

      <Container>
        <TitleWrapper>
          <PageTitle>клиенты</PageTitle>

          <SearchBlock onOpenAddModal={openAddModal} control={control} />
        </TitleWrapper>
        <TableHeader />
        <ClientsTable
          onArchiveClient={onArchiveClient}
          onEditClient={onClickOpenEditModal}
          isLoading={isLoading}
          onLoadMore={onLoadMore}
          clients={clientsResponse?.data}
          hasNextPage={clientsResponse?.meta.hasNextPage}
          isArchiveClientAvailable={isArchiveClientAvailable}
        />
      </Container>
    </>
  )
}
